import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useBoard } from "@/hooks/useBoard";
import statusBaked from "@/data/statusBoard.json";
import { Card, Label, MonoMeta } from "@/design/primitives";
import { cn } from "@/lib/utils";

// ── Status — the detail behind the top band (Jeff 2026-10-24) ─────────────
// The band shows the short version always visible at the top of every screen;
// this page carries the full detail it summarizes, in the same order:
//   1. AI PROXIES   — every Ollama proxy key, the detail the old Command Center
//                     had: session / weekly / cycle usage, resets, cooling
//   2. AI BURN      — OpenRouter, per day and per model (our API keys)
//   3. REVENUE      — Stripe accounts + PayStand (the direct-pay accounts),
//                     by brand, with who purchased
//
// Truth rules (inherited from the band, JOB-20261023-OS-TOPBAR): the board
// never invents a number — a degraded half renders its own honest "no data",
// unmatched charges stay unmatched with their amounts, never guessed brands.

const money = (usd) => {
  if (usd === null || usd === undefined || Number.isNaN(Number(usd)))
    return "—";
  const n = Number(usd);
  const cents = Math.abs(n % 1) > 0.004;
  return `$${n.toLocaleString("en-US", {
    minimumFractionDigits: cents ? 2 : 0,
    maximumFractionDigits: cents ? 2 : 0,
  })}`;
};

const dayLabel = (day) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(day || ""));
  if (!m) return String(day || "");
  return new Date(
    Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])),
  ).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
};

const pctText = (pct) =>
  pct === null || pct === undefined || Number.isNaN(Number(pct))
    ? "—"
    : `${Math.round(Number(pct))}%`;

const ageLabel = (iso) => {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  const mins = Math.max(0, Math.round((Date.now() - t) / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  return `${Math.round(mins / 60)}h ago`;
};

const clockTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
};

const TH = ({ children, className }) => (
  <th
    className={cn(
      "border-b border-os-border pb-2 pr-3 text-left font-mono text-[0.625rem] font-medium uppercase tracking-[0.08em] text-os-ink-4",
      className,
    )}
  >
    {children}
  </th>
);
const TD = ({ children, className }) => (
  <td
    className={cn(
      "border-b border-os-border/60 py-2.5 pr-3 align-middle text-os-ink-2",
      className,
    )}
  >
    {children}
  </td>
);

// Jeff 2026-10-26: newest day first, everywhere a table lists days —
// he reads the burn/revenue lists from the top (today, yesterday, …),
// not from a scrolled bottom row.
const newestFirst = (arr) => [...arr].sort((a, b) => String(b.day).localeCompare(String(a.day)));

const SectionTitle = ({ id, title, note }) => (
  <div
    id={id}
    className="mb-3 flex scroll-mt-20 flex-wrap items-baseline gap-x-3"
  >
    <h2 className="font-mono text-[0.6875rem] uppercase tracking-[0.1em] text-os-ink-2">
      {title}
    </h2>
    {note ? (
      <span className="text-[0.625rem] text-os-ink-4">{note}</span>
    ) : null}
  </div>
);

const NoData = ({ children }) => (
  <div className="rounded-chip border border-os-border bg-os-paper px-4 py-5 text-center text-[0.75rem] text-os-ink-4">
    {children || "no data — the source is down, nothing is invented"}
  </div>
);

// ── Hover drill-down (JOB-20261103-STATUS-HOVER, Jeff 2026-11-03) ──────────
// Any bar/shape cell can carry a breakdown; hovering (or focusing — keyboard
// and touch both work) floats a plain list of exactly what made that day's
// number: which model spent what, which worker served what. Native CSS
// (group-hover / group-focus-within), no portal, right-aligned over the
// table. Truth rules apply: no rows → renders the plain bar, no tooltip at
// all (honest absence), never an empty popup.
const HoverPop = ({ rows, label, testid, children }) => {
  const items = (Array.isArray(rows) ? rows : []).filter(Boolean);
  if (items.length === 0) return children;
  return (
    <span className="group relative flex w-full items-center">
      <span
        tabIndex={0}
        role="button"
        aria-label={label || "Show what made up this total"}
        data-testid={testid}
        className="relative min-w-0 flex-1 cursor-help outline-none"
      >
        {children}
        <span
          role="tooltip"
          data-testid={testid ? `${testid}-pop` : undefined}
          className="pointer-events-none absolute bottom-full right-0 z-30 mb-1.5 hidden min-w-56 max-w-72 rounded-chip border border-os-border bg-white px-3 py-2 shadow-lg group-hover:block group-focus-within:block"
        >
          {items.map((r, i) => (
            <span
              key={i}
              className="flex items-baseline justify-between gap-3 py-0.5 text-[0.6875rem] leading-snug"
            >
              <span className="min-w-0 truncate font-mono text-os-ink-2">
                {r.left}
              </span>
              <span className="shrink-0 font-mono tabular-nums text-os-ink">
                {r.right}
              </span>
            </span>
          ))}
        </span>
      </span>
    </span>
  );
};

// ── 1. AI proxies ──
const ProxyTable = ({ ollama }) => {
  const accounts = Array.isArray(ollama?.accounts) ? ollama.accounts : [];
  if (ollama?.degraded || accounts.length === 0) {
    return (
      <NoData>{ollama?.error ? `no data (${ollama.error})` : undefined}</NoData>
    );
  }
  // Jeff 2026-10-25 (bars) revised 2026-10-26; revised 2026-10-29 (Jeff, 2nd
  // ruling: % + color everywhere — % of the REAL plan allowance, clamped 0..100):
  //   • Session bar — share of the busiest key's session (relative shape).
  //     It answers "who is working hardest right now", NOT "how full is the
  //     tank"; the caption says so, and a zero/unknown value draws no bar.
  //   • Cycle bar — % of the account's REAL plan allowance (the backend
  //     clamps 0..100; an ollama.com 429 cap forces exactly 100%, amber).
  const maxSession = accounts.reduce(
    (m, a) => Math.max(m, Number(a.sessionUsage) || 0),
    0,
  );

  const UsageBar = ({ usd, max, testid }) => {
    const n = Number(usd) || 0;
    const pct = max > 0 && n > 0 ? Math.max(4, Math.round((n / max) * 100)) : 0;
    return (
      <span
        data-testid={testid}
        aria-hidden="true"
        className="flex h-1.5 w-full justify-end overflow-hidden rounded-segment bg-os-border/40"
      >
        {pct > 0 ? (
          <span
            className="block h-1.5 rounded-segment bg-os-ink"
            style={{ width: `${pct}%` }}
          />
        ) : null}
      </span>
    );
  };
  // 2026-10-29: AllowanceBar retired — ollama.com exposes no allowance; bars
  // are relative shares only (UsageBar). Kept out of the render entirely.
  return (
    <div className="overflow-x-auto pt-1">
      <table className="w-full min-w-[840px] text-[0.8125rem]">
        <thead>
          <tr>
            <TH>Key</TH>
            <TH>Plan</TH>
            <TH>State</TH>
            <TH className="text-right">Session</TH>
            <TH className="w-[14%]">Session share</TH>
            <TH className="text-right">This cycle</TH>
            <TH className="w-[14%]">Cycle share</TH>
            <TH>Session resets</TH>
            <TH>Cycle resets</TH>
          </tr>
        </thead>
        <tbody>
          {accounts.map((a) => {
            const pct =
              a.limitHit
                ? 100
                : a.weeklyQuotaPct != null
                  ? Math.round(Number(a.weeklyQuotaPct))
                  : null;
            const tone = a.cooling || a.limitHit ? "amber" : "green";
            return (
            <tr key={a.label} data-testid={`status-proxy-${a.label}`}>
              <TD className="font-mono text-os-ink">{a.label}</TD>
              <TD>{a.tier || "—"}</TD>
              <TD>
                <span
                  className="inline-flex items-center gap-1.5"
                  data-testid={`status-proxy-state-${a.label}`}
                  data-tone={tone}
                >
                  <span
                    aria-hidden="true"
                    className={cn(
                      "h-1.5 w-1.5 rounded-full",
                      tone === "amber" ? "bg-os-amber" : "bg-os-green",
                    )}
                  />
                  {/* Jeff 10/29 (2nd ruling): % + color only — % of the REAL
                      plan allowance; a 429-capped key reads exactly 100% amber. */}
                  {pct != null ? `${pct}%` : "—"}
                </span>
              </TD>
              <TD className="text-right tabular-nums">
                {a.sessionUsage != null
                  ? a.sessionUsage.toLocaleString("en-US")
                  : "—"}
              </TD>
              <TD>
                <UsageBar
                  usd={a.sessionUsage}
                  max={maxSession}
                  testid={`status-proxy-session-bar-${a.label}`}
                />
              </TD>
              <TD className="text-right tabular-nums">
                {a.cycleUsage != null
                  ? a.cycleUsage.toLocaleString("en-US")
                  : "—"}
              </TD>
              <TD>
                {pct != null ? (
                  <span
                    data-testid={`status-proxy-cycle-bar-${a.label}`}
                    aria-hidden="true"
                    className="flex h-1.5 w-full justify-end overflow-hidden rounded-segment bg-os-border/40"
                  >
                    <span
                      className={cn(
                        "block h-1.5 rounded-segment",
                        (a.cooling || a.limitHit) ? "bg-os-amber" : "bg-os-ink",
                      )}
                      style={{ width: `${Math.max(2, Math.min(100, pct))}%` }}
                    />
                  </span>
                ) : (
                  <span className="text-os-ink-4">—</span>
                )}
              </TD>
              <TD>{a.sessionResetsIn || "—"}</TD>
              <TD>{a.cycleResetsIn || "—"}</TD>
            </tr>
            );
          })}
        </tbody>
      </table>
      <p
        data-testid="status-proxy-usagenote"
        className="mt-1.5 text-[0.625rem] leading-relaxed text-os-ink-4"
      >
        &ldquo;This cycle&rdquo; counts requests since the Sunday 6pm MDT quota
        reset. % = requests this cycle ÷ the account&rsquo;s real weekly
        allowance (MAX ≈ 32,768 req/wk, PRO ≈ 8,111 req/wk, read off the
        ollama.com dashboard). A key ollama.com itself caps reads 100% amber —
        its own 429 is the ground truth, not an estimate.
      </p>
    </div>
  );
};

// ── 2. AI burn ──
const BurnTable = ({ openrouter }) => {
  // Jeff 2026-10-26: newest first (today at the top, not the oldest date)
  const orDays = newestFirst(
    Array.isArray(openrouter?.days) ? openrouter.days : [],
  );
  if (!openrouter || openrouter.degraded || orDays.length === 0)
    return <NoData />;
  const maxUsd = orDays.reduce((m, d) => Math.max(m, Number(d.usd) || 0), 0);
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <div>
        <p className="mb-1.5 font-mono text-[0.625rem] uppercase tracking-[0.08em] text-os-ink-4">
          7-day total {money(openrouter.total7dUsd)}
        </p>
        <table className="w-full text-[0.8125rem]">
          <thead>
            <tr>
              <TH>Day</TH>
              <TH className="text-right">Spend</TH>
              <TH className="w-1/2">Shape</TH>
            </tr>
          </thead>
          <tbody>
            {orDays.map((d, i) => {
              const usd = Number(d.usd) || 0;
              // Job-20261103 hover drill-down: the shape bar explains itself —
              // hover (or focus) lists which model spent what that day.
              const modelRows = (Array.isArray(d.models) ? d.models : [])
                .filter((m) => m && ((Number(m.cost) || 0) > 0 || (Number(m.calls) || 0) > 0))
                .map((m) => ({
                  left: String(m.model || "unknown"),
                  right:
                    (Number(m.cost) || 0) > 0
                      ? `${money(m.cost)} · ${(Number(m.calls) || 0).toLocaleString("en-US")} calls`
                      : `${(Number(m.calls) || 0).toLocaleString("en-US")} calls · $0`,
                }));
              return (
                <tr key={d.day || i} data-testid={`status-or-day-${d.day}`}>
                  <TD>{dayLabel(d.day)}</TD>
                  <TD className="text-right tabular-nums text-os-ink">
                    {usd > 0 ? money(usd) : "—"}
                  </TD>
                  <TD>
                    <HoverPop
                      rows={modelRows}
                      testid={`status-or-pop-${d.day}`}
                      label={`Which models spent the $${usd.toFixed(2)} on ${d.day}`}
                    >
                      <span
                        aria-hidden="true"
                        data-testid={`status-or-shape-${d.day}`}
                        className="flex h-1.5 w-full justify-end overflow-hidden rounded-segment bg-os-border/40"
                      >
                        {usd > 0 ? (
                          <span
                            className="block h-1.5 rounded-segment bg-os-ink"
                            style={{
                              width: `${Math.max(3, Math.round((usd / (maxUsd || 1)) * 100))}%`,
                            }}
                          />
                        ) : null}
                      </span>
                    </HoverPop>
                  </TD>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div>
        <p className="mb-1.5 font-mono text-[0.625rem] uppercase tracking-[0.08em] text-os-ink-4">
          Top models, 7 days
        </p>
        <table className="w-full text-[0.8125rem]">
          <tbody>
            {(openrouter.models || []).map((m2) => (
              <tr key={m2.model}>
                <TD className="truncate font-mono text-[0.75rem]">
                  {m2.model}
                </TD>
                <TD className="text-right tabular-nums">
                  {m2.calls != null
                    ? `${m2.calls.toLocaleString("en-US")} calls`
                    : ""}
                </TD>
                <TD className="text-right tabular-nums text-os-ink">
                  {money(m2.total_cost)}
                </TD>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ── 2b. Cloudflare (free-tier Workers usage; JOB-20261103-CC-FRESH) ──
// 2026-11-03 (JOB-20261103-CF-TOKEN): zone analytics unlocked (Jeff rolled the
// token with Zone Analytics Read) — per-domain traffic table added underneath.
// Truth rules unchanged: a missing zone feed renders nothing invented.
const CloudflarePanel = ({ cloudflare }) => {
  const cfDays = newestFirst(
    Array.isArray(cloudflare?.days) ? cloudflare.days : [],
  );
  const zones = Array.isArray(cloudflare?.zonesTraffic)
    ? cloudflare.zonesTraffic
    : [];
  const aiRows = Array.isArray(cloudflare?.aiNeurons)
    ? cloudflare.aiNeurons
    : [];
  if (!cloudflare || cloudflare.degraded || cfDays.length === 0)
    return <NoData />;
  const maxN = cfDays.reduce((m, d) => Math.max(m, Number(d.workers) || 0), 0);
  const maxZ = zones.reduce((m, z) => Math.max(m, Number(z.requests7d) || 0), 0);
  return (
    <div>
      {/* Jeff 2026-11-03: AI tokens first — the spend he actually watches. */}
      {aiRows.length > 0 ? (
        <div>
          <p className="mb-1.5 font-mono text-[0.625rem] uppercase tracking-[0.08em] text-os-ink-4">
            AI tokens (free allowance) — Cloudflare Workers AI, neurons spent
            per day vs{" "}
            {Number(
              aiRows[0]?.allowance ?? 10000,
            ).toLocaleString("en-US")}
            /day free
          </p>
          <table className="w-full text-[0.8125rem]">
            <thead>
              <tr>
                <TH>Day</TH>
                <TH className="text-right">Neurons</TH>
                <TH className="w-1/2">Share of free/day</TH>
              </tr>
            </thead>
            <tbody>
              {newestFirst(aiRows).map((r) => {
                const n = Number(r.neurons) || 0;
                const allow = Number(r.allowance) || 10000;
                const pct = Math.min(100, Math.round((n / allow) * 100));
                const modelRows = (Array.isArray(r.models) ? r.models : [])
                  .filter((m) => m && (Number(m.neurons) || 0) > 0)
                  .map((m) => ({
                    left: String(m.model || "unknown"),
                    right: `${(Number(m.neurons) || 0).toLocaleString("en-US")} neurons`,
                  }));
                return (
                  <tr key={r.day} data-testid={`status-cf-ai-${r.day}`}>
                    <TD>{dayLabel(r.day)}</TD>
                    <TD className="text-right tabular-nums text-os-ink">
                      {n > 0 ? n.toLocaleString("en-US") : "—"}
                    </TD>
                    <TD>
                      <HoverPop
                        rows={modelRows}
                        testid={`status-cf-ai-pop-${r.day}`}
                        label={`Which models spent the ${n.toLocaleString("en-US")} neurons on ${r.day}`}
                      >
                        <span
                          aria-hidden="true"
                          className="flex h-1.5 w-full justify-end overflow-hidden rounded-segment bg-os-border/40"
                        >
                          {n > 0 ? (
                            <span
                              className="block h-1.5 rounded-segment bg-os-ink"
                              style={{
                                width: `${Math.max(3, pct)}%`,
                              }}
                            />
                          ) : null}
                        </span>
                      </HoverPop>
                    </TD>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
      <p
        className={
          aiRows.length > 0
            ? "mb-1.5 mt-4 font-mono text-[0.625rem] uppercase tracking-[0.08em] text-os-ink-4"
            : "mb-1.5 font-mono text-[0.625rem] uppercase tracking-[0.08em] text-os-ink-4"
        }
      >
        App code usage (free tier) — invocations, 7-day total{" "}
        {Number(cloudflare.workersTotal || 0).toLocaleString("en-US")} (free
        tier: 100k/day). Hover a bar to see which worker (app) served the
        requests.
      </p>
      <table className="w-full text-[0.8125rem]">
        <thead>
          <tr>
            <TH>Day</TH>
            <TH className="text-right">Invocations</TH>
            <TH className="w-1/2">Shape</TH>
          </tr>
        </thead>
        <tbody>
          {cfDays.map((d, i) => {
            const n = Number(d.workers) || 0;
            const scriptRows = (Array.isArray(d.scripts) ? d.scripts : [])
              .filter((s) => s && (Number(s.requests) || 0) > 0)
              .map((s) => ({
                left: String(s.name || "unknown"),
                right: `${(Number(s.requests) || 0).toLocaleString("en-US")} inv`,
              }));
            return (
              <tr key={d.day || i} data-testid={`status-cf-day-${d.day}`}>
                <TD>{dayLabel(d.day)}</TD>
                <TD className="text-right tabular-nums text-os-ink">
                  {n > 0 ? n.toLocaleString("en-US") : "—"}
                </TD>
                <TD>
                  <HoverPop
                    rows={scriptRows}
                    testid={`status-cf-pop-${d.day}`}
                    label={`Which workers served the ${n.toLocaleString("en-US")} invocations on ${d.day}`}
                  >
                    <span
                      aria-hidden="true"
                      className="flex h-1.5 w-full justify-end overflow-hidden rounded-segment bg-os-border/40"
                    >
                      {n > 0 ? (
                        <span
                          className="block h-1.5 rounded-segment bg-os-ink"
                          style={{
                            width: `${Math.max(3, Math.round((n / (maxN || 1)) * 100))}%`,
                          }}
                        />
                      ) : null}
                    </span>
                  </HoverPop>
                </TD>
              </tr>
            );
          })}
        </tbody>
      </table>
      {zones.length > 0 ? (
        <div className="mt-4">
          <p className="mb-1.5 font-mono text-[0.625rem] uppercase tracking-[0.08em] text-os-ink-4">
            Site visits (unmetered) — requests per domain, 7-day
          </p>
          <table className="w-full text-[0.8125rem]">
            <thead>
              <tr>
                <TH>Domain</TH>
                <TH className="text-right">Requests 7d</TH>
                <TH className="w-1/2">Shape</TH>
              </tr>
            </thead>
            <tbody>
              {zones.map((z) => {
                const n = Number(z.requests7d) || 0;
                return (
                  <tr key={z.domain} data-testid={`status-cf-zone-${z.domain}`}>
                    <TD className="font-mono text-[0.75rem] text-os-ink">
                      {z.domain}
                    </TD>
                    <TD className="text-right tabular-nums text-os-ink">
                      {n > 0 ? n.toLocaleString("en-US") : "—"}
                    </TD>
                    <TD>
                      <span
                        aria-hidden="true"
                        className="flex h-1.5 w-full justify-end overflow-hidden rounded-segment bg-os-border/40"
                      >
                        {n > 0 ? (
                          <span
                            className="block h-1.5 rounded-segment bg-os-ink"
                            style={{
                              width: `${Math.max(3, Math.round((n / (maxZ || 1)) * 100))}%`,
                            }}
                          />
                        ) : null}
                      </span>
                    </TD>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
};

// ── 3. Revenue ──
const RevenuePanels = ({ revenue, sales }) => {
  if (
    !revenue ||
    (revenue.degraded &&
      !(revenue?.brands30d && Object.keys(revenue.brands30d).length))
  ) {
    return <NoData />;
  }
  const windows = revenue.windows || {};
  // One table, two windows (council: 90d is a superset of 30d — folding kills the
  // duplicate table and makes the trend readable in one sweep).
  const brands = Array.from(
    new Set([
      ...Object.keys(windows.last_30_days || {}),
      ...Object.keys(windows.last_90_days || {}),
    ]),
  )
    .map((b) => ({
      brand: b,
      count30: windows.last_30_days?.[b]?.count || 0,
      usd30: windows.last_30_days?.[b]?.usd || 0,
      usd90: windows.last_90_days?.[b]?.usd || 0,
      count90: windows.last_90_days?.[b]?.count || 0,
    }))
    .filter((r) => r.usd30 > 0 || r.usd90 > 0)
    .sort((a, b) => Math.max(b.usd30, b.usd90) - Math.max(a.usd30, a.usd90));
  const recent = (revenue.recent || []).slice(0, 12);

  // Sales (the band's 5-day figure) gets its drill-down home here (council R2-2).
  // Jeff 2026-10-26: newest day first, same as the burn table.
  const saleDays = newestFirst(Array.isArray(sales?.days) ? sales.days : []);
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <div className="space-y-4">
        <div id="sales" className="scroll-mt-20" data-testid="status-sales">
          <p className="mb-1.5 font-mono text-[0.625rem] uppercase tracking-[0.08em] text-os-ink-4">
            Last 5 days — new purchases (the band&apos;s SALES figure)
          </p>
          {saleDays.length === 0 ? (
            <NoData>no sales data</NoData>
          ) : (
            <table
              className="w-full text-[0.8125rem]"
              data-testid="status-sales-table"
            >
              <thead>
                <tr>
                  <TH>Day</TH>
                  <TH>Who bought</TH>
                  <TH className="text-right">Total</TH>
                </tr>
              </thead>
              <tbody>
                {saleDays.map((d, i) => (
                  <tr key={d.day || i} data-testid={`status-sales-${d.day}`}>
                    <TD className="whitespace-nowrap">{dayLabel(d.day)}</TD>
                    <TD>
                      {(d.items || []).length === 0 ? (
                        <span className="text-os-ink-4">no sales</span>
                      ) : (
                        (d.items || []).map((it, j) => (
                          <span
                            key={j}
                            className="mr-3 whitespace-nowrap text-os-ink"
                          >
                            {it.name} {money(it.usd)}
                            {it.count > 1 ? ` ×${it.count}` : ""}
                          </span>
                        ))
                      )}
                    </TD>
                    <TD className="text-right tabular-nums text-os-ink">
                      {money(d.totalUsd)}
                    </TD>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div>
          <p className="mb-1.5 font-mono text-[0.625rem] uppercase tracking-[0.08em] text-os-ink-4">
            By brand — 30d vs 90d
          </p>
          <table
            className="w-full text-[0.8125rem]"
            data-testid="status-rev-30d"
          >
            <thead>
              <tr>
                <TH>Brand</TH>
                <TH className="text-right">Purchases 30d</TH>
                <TH className="text-right">Gross 30d</TH>
                <TH className="text-right">Gross 90d</TH>
              </tr>
            </thead>
            <tbody>
              {brands.length === 0 ? (
                <tr>
                  <TD colSpan={4} className="text-os-ink-4">
                    no purchases in the last 90 days
                  </TD>
                </tr>
              ) : (
                brands.map((r) => (
                  <tr
                    key={r.brand}
                    data-testid={`status-rev-30d-${r.brand.replace(/\W+/g, "-")}`}
                    className="transition-colors hover:bg-os-paper"
                  >
                    <TD className="text-os-ink">
                      <Link
                        to="/projects"
                        className="hover:underline"
                        title={`Open the projects board (where ${r.brand} work lives)`}
                      >
                        {r.brand}
                      </Link>
                    </TD>
                    <TD className="text-right tabular-nums">{r.count30}</TD>
                    <TD className="text-right tabular-nums text-os-ink">
                      {money(r.usd30)}
                    </TD>
                    <TD className="text-right tabular-nums">
                      {money(r.usd90)}
                    </TD>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <p className="text-[0.625rem] text-os-ink-4">
          Stripe ({revenue.sources?.stripe || "unknown"}) + PayStand (
          {revenue.sources?.paystand || "unknown"}); unmatched charges keep
          their own line — never guessed into a brand.
        </p>
      </div>
      <div>
        <p className="mb-1.5 font-mono text-[0.625rem] uppercase tracking-[0.08em] text-os-ink-4">
          Recent purchases (who · what · where)
        </p>
        {recent.length === 0 ? (
          <NoData>no purchases recorded in the recent window</NoData>
        ) : (
          <table
            className="w-full text-[0.8125rem]"
            data-testid="status-rev-recent"
          >
            <thead>
              <tr>
                <TH>Day</TH>
                <TH>Brand · product</TH>
                <TH>Buyer</TH>
                <TH>Invoice / ref</TH>
                <TH className="text-right">Amount</TH>
              </tr>
            </thead>
            <tbody>
              {recent.map((t, i) => (
                <tr key={`${t.day}-${t.product}-${i}`}>
                  <TD className="whitespace-nowrap">{dayLabel(t.day)}</TD>
                  <TD>
                    <span className="font-medium text-os-ink">{t.brand}</span>
                    {t.product && t.product !== t.brand ? (
                      <span className="block text-[0.6875rem] text-os-ink-4">
                        {t.product}
                      </span>
                    ) : null}
                  </TD>
                  <TD>{t.who || <span className="text-os-ink-4">—</span>}</TD>
                  <TD className="font-mono text-[0.6875rem] text-os-ink-2">
                    {t.invoice || t.ref || (
                      <span className="text-os-ink-4">—</span>
                    )}
                  </TD>
                  <TD className="text-right tabular-nums text-os-ink">
                    {money(t.usd)}
                  </TD>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export const StatusPage = () => {
  const board = useBoard("status");
  const item = useMemo(() => {
    if (board.live)
      return (
        (board.items || []).find((r) => r.slug === "status:topbar") || null
      );
    return (
      (statusBaked.items || []).find((r) => r.slug === "status:topbar") || null
    );
  }, [board.items, board.live]);
  const p = item?.payload || {};
  const asOf = p.ollama?.asOf || p.sales?.asOf || p.pushedAt || null;

  return (
    <div
      className="mx-auto max-w-6xl px-4 py-6 md:px-8"
      data-testid="status-page"
    >
      <header className="mb-6">
        <h1 className="font-mono text-[0.8125rem] font-semibold uppercase tracking-[0.08em] text-os-ink">
          Status — the band, in full
        </h1>
        <p className="mt-1.5 max-w-2xl text-[0.75rem] leading-relaxed text-os-ink-4">
          The short version rides the top bar on every screen. This is the whole
          picture: the AI proxies, the OpenRouter burn behind them, and every
          dollar that came in. Data as of <MonoMeta>{clockTime(asOf)}</MonoMeta>
          .
        </p>
      </header>

      {/* Jump rail (council): the page moves in-page, not just by scrolling. */}
      <nav
        data-testid="status-jump-rail"
        aria-label="Jump to a status section"
        className="sticky top-9 z-10 -mx-4 mb-4 flex gap-2 border-b border-os-border bg-white/95 px-4 py-2 font-mono text-[0.625rem] uppercase tracking-[0.08em] backdrop-blur-sm md:-mx-8 md:px-8"
      >
        {[
          ["#status-proxies", "Proxies"],
          ["#status-burn", "AI burn"],
          ["#status-cloudflare", "Cloudflare"],
          ["#sales", "Sales 5d"],
          ["#status-revenue", "Revenue"],
        ].map(([href, label]) => (
          <a
            key={href}
            href={href}
            className="rounded-chip px-2 py-1 text-os-ink-3 hover:bg-os-paper hover:text-os-ink"
          >
            {label}
          </a>
        ))}
        <span className="ml-auto self-center text-os-ink-4 normal-case tracking-normal">
          data age {ageLabel(asOf) || "—"}
        </span>
      </nav>

      <div className="space-y-8">
        <Card className="px-5 py-4 sm:px-6 sm:py-5">
          <SectionTitle
            id="status-proxies"
            title="AI proxies"
            note="Ollama keys: session · weekly · cycle — per-model detail isn't exposed by ollama.com's API, so a hover split isn't possible here"
          />
          <ProxyTable ollama={p.ollama} />
        </Card>
        <Card className="px-5 py-4 sm:px-6 sm:py-5">
          <SectionTitle
            id="status-burn"
            title="AI burn — OpenRouter"
            note="our keys, per day — hover a bar for the model split"
          />
          <BurnTable openrouter={p.openrouter} />
        </Card>
        <Card className="px-5 py-4 sm:px-6 sm:py-5">
          <SectionTitle
            id="status-cloudflare"
            title="Cloudflare — AI tokens, app usage, domain traffic"
            note="free tier · hover any bar for its breakdown"
          />
          <CloudflarePanel cloudflare={p.cloudflare} />
        </Card>
        <Card className="px-5 py-4 sm:px-6 sm:py-5">
          <SectionTitle
            id="status-revenue"
            title="Revenue"
            note="Stripe + PayStand · by brand · who purchased"
          />
          <RevenuePanels revenue={p.revenue} sales={p.sales} />
        </Card>
      </div>
    </div>
  );
};

export default StatusPage;
